import { FileText, Files, History, UserCheck } from 'lucide-react';
import { useId, useState } from 'react';
import type { CreatorActivityItem } from '../lib/creatorActivity';
import { formatDate, packPath, profilePath } from '../lib/navigation';
import { profileVersionPath } from '../lib/profileVersions';
import { DeviceIcon } from './DeviceIcon';
import { CreatorAvatar } from './CreatorAvatar';
import { CreatorLink } from './CreatorLink';
import { Link } from './Link';
import { ReleaseStatus } from './ReleaseStatus';

type ActivityFilter = 'all' | 'updates' | 'uploads';
type ActivityTypeFilter = 'all' | CreatorActivityItem['kind'];
const FILTERS: Array<{ value: ActivityFilter; label: string }> = [
  { value: 'all', label: 'All activity' },
  { value: 'updates', label: 'Updates' },
  { value: 'uploads', label: 'Uploads' },
];
const TYPE_FILTERS: Array<{ value: ActivityTypeFilter; label: string }> = [
  { value: 'all', label: 'All types' },
  { value: 'profile', label: 'Profiles' },
  { value: 'pack', label: 'Profile packs' },
];
const PAGE_SIZE = 6;

function packChanges(item: Extract<CreatorActivityItem, { kind: 'pack' }>): string {
  const changes: string[] = [];
  if (item.revisedCount) changes.push(`${item.revisedCount} profile${item.revisedCount === 1 ? '' : 's'} revised`);
  if (item.addedCount) changes.push(`${item.addedCount} profile${item.addedCount === 1 ? '' : 's'} added`);
  return changes.join(' · ');
}

interface CreatorActivityProps {
  items: CreatorActivityItem[];
  returnTo: string;
  variant?: 'creator' | 'following';
}

export function CreatorActivity({ items, returnTo, variant = 'creator' }: CreatorActivityProps) {
  const headingId = useId();
  const isFollowingFeed = variant === 'following';
  const [filter, setFilter] = useState<ActivityFilter>('all');
  const [typeFilter, setTypeFilter] = useState<ActivityTypeFilter>('all');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const filtered = items.filter((item) =>
    (typeFilter === 'all' || item.kind === typeFilter)
    && (filter === 'all' || (filter === 'updates' ? item.isUpdate : !item.isUpdate)),
  );
  const visible = filtered.slice(0, visibleCount);
  const emptyMessage = typeFilter !== 'all'
    ? `No ${typeFilter === 'pack' ? 'profile pack' : 'profile'} ${filter === 'all' ? 'activity has' : `${filter} have`} been recorded yet.`
    : filter === 'updates'
      ? 'No revisions or pack additions have been recorded yet.'
      : filter === 'uploads' ? 'No uploads have been recorded yet.' : 'No activity has been recorded yet.';

  return (
    <section className={`content-section creator-content-section creator-activity${isFollowingFeed ? ' home-section following-activity' : ''}`} aria-labelledby={headingId}>
      <div className="section-heading creator-activity-heading">
        <div>
          <h2 id={headingId}>{isFollowingFeed ? 'Followed Creators' : 'Latest activity'}</h2>
          <p>{isFollowingFeed ? 'New uploads and updates from creators you follow.' : 'New uploads and updates, newest first.'}</p>
        </div>
        {items.length > 0 && <div className="activity-filter-controls">
          <div className="activity-filters" role="group" aria-label="Filter activity by type">
            {TYPE_FILTERS.map(({ value, label }) => (
              <button type="button" key={value} aria-pressed={typeFilter === value} onClick={() => { setTypeFilter(value); setVisibleCount(PAGE_SIZE); }}>
                {value === 'profile' && <FileText size={15} aria-hidden="true" />}
                {value === 'pack' && <Files size={15} aria-hidden="true" />}
                {label}
              </button>
            ))}
          </div>
          <div className="activity-filters" role="group" aria-label="Filter creator activity">
            {FILTERS.map(({ value, label }) => (
              <button type="button" key={value} aria-pressed={filter === value} onClick={() => { setFilter(value); setVisibleCount(PAGE_SIZE); }}>{label}</button>
            ))}
          </div>
        </div>}
      </div>
      {visible.length > 0 ? (
        <ol className="creator-activity-list">
          {visible.map((item) => {
            const href = item.kind === 'pack' ? packPath(item.record) : item.version ? profileVersionPath(item.record, item.version) : profilePath(item.record);
            const changes = item.kind === 'pack' && item.isUpdate ? packChanges(item) : '';
            const description = item.exampleNote || changes;
            const typeLabel = item.kind === 'pack'
              ? `Profile pack containing ${item.record.profileCount} profile${item.record.profileCount === 1 ? '' : 's'}`
              : 'Individual profile';
            return (
              <li className="creator-activity-row" key={`${item.kind}:${item.record.id}:${item.date}:${item.exampleNote ? 'example' : 'recorded'}`}>
                {isFollowingFeed
                  ? <CreatorAvatar name={item.record.creator} className="activity-avatar" />
                  : <DeviceIcon device={item.record.device} compact />}
                <div className="activity-content">
                  {isFollowingFeed && <div className="activity-author">
                    <CreatorLink name={item.record.creator} />
                    <span className="activity-following"><UserCheck size={13} aria-hidden="true" />Following</span>
                  </div>}
                  <p className="activity-meta">
                    <span className="activity-device">{isFollowingFeed && <DeviceIcon device={item.record.device} compact />}{item.record.device}</span>
                  </p>
                  <div className="activity-title">
                    <span className={`activity-type activity-type-${item.kind}`} role="img" aria-label={typeLabel} title={typeLabel}>
                      {item.kind === 'pack'
                        ? <><Files size={17} aria-hidden="true" /><span aria-hidden="true">{item.record.profileCount}</span></>
                        : <FileText size={17} aria-hidden="true" />}
                    </span>
                    <h3>
                      <Link href={href} navigationState={item.kind === 'profile' || isFollowingFeed ? { returnTo, fromSearch: true } : undefined}>{item.record.name}</Link>
                      {item.version && <span className="activity-version" aria-label={`Version ${item.version}`}>v{item.version}</span>}
                    </h3>
                  </div>
                  {description && <p className="activity-description">{description}</p>}
                </div>
                <div className="activity-date">
                  <ReleaseStatus updated={item.isUpdate} />
                  <time dateTime={item.date}>{formatDate(item.date)}</time>
                </div>
              </li>
            );
          })}
        </ol>
      ) : isFollowingFeed && items.length === 0 ? (
        <div className="following-empty">
          <History size={24} aria-hidden="true" />
          <div><h3>No followed creators yet</h3><p>Follow a creator to see their latest uploads and revisions here.</p></div>
          <Link className="button secondary" href="/search">Browse profiles</Link>
        </div>
      ) : (
        <div className="empty-note" role="status"><History size={20} /><p>{emptyMessage}</p></div>
      )}
      {filtered.length > visibleCount && <button className="button ghost creator-show-all" type="button" onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}>Show more activity</button>}
    </section>
  );
}
